/**
 * AuditYearContext — Feature 1.2 (revised).
 *
 * Provides the globally selected audit year across the entire app.
 * The selected year is persisted in localStorage so it survives page refresh.
 *
 * Usage:
 *   const { selectedAuditYear, setSelectedAuditYearId, auditYears } = useAuditYear();
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import type { AuditYear } from '../api/auditYears.ts';

const STORAGE_KEY = 'skeptics:selectedAuditYearId';

interface AuditYearContextValue {
  /** All audit years loaded for the org, newest first */
  auditYears: AuditYear[];
  setAuditYears: (years: AuditYear[]) => void;
  /** The currently selected audit year object (null while loading) */
  selectedAuditYear: AuditYear | null;
  /** The ID stored in localStorage */
  selectedAuditYearId: string | null;
  setSelectedAuditYearId: (id: string) => void;
}

const AuditYearContext = createContext<AuditYearContextValue | null>(null);

export function AuditYearProvider({ children }: { children: React.ReactNode }) {
  const [auditYears, setAuditYearsState] = useState<AuditYear[]>([]);
  const [selectedAuditYearId, setSelectedAuditYearIdState] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY)
  );

  const setAuditYears = useCallback((years: AuditYear[]) => {
    setAuditYearsState(years);
    // Auto-select the first active non-locked year if nothing is stored or the stored ID is gone.
    setSelectedAuditYearIdState((prev) => {
      if (prev && years.some((y) => y.id === prev)) return prev;
      const activeYear = years.find((y) => y.is_active && !y.is_locked) ?? years[0] ?? null;
      const nextId = activeYear?.id ?? null;
      if (nextId) {
        localStorage.setItem(STORAGE_KEY, nextId);
      } else {
        // No valid year exists — clear stale UUID so deleted-year ID doesn't persist across reloads.
        localStorage.removeItem(STORAGE_KEY);
      }
      return nextId;
    });
  }, []);

  const setSelectedAuditYearId = useCallback((id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setSelectedAuditYearIdState(id);
  }, []);

  const selectedAuditYear = auditYears.find((y) => y.id === selectedAuditYearId) ?? null;

  return (
    <AuditYearContext.Provider
      value={{ auditYears, setAuditYears, selectedAuditYear, selectedAuditYearId, setSelectedAuditYearId }}
    >
      {children}
    </AuditYearContext.Provider>
  );
}

/** Like `useAuditYear`, but returns null outside an `<AuditYearProvider>` instead of throwing. */
export function useOptionalAuditYear(): AuditYearContextValue | null {
  return useContext(AuditYearContext);
}

export function useAuditYear(): AuditYearContextValue {
  const ctx = useContext(AuditYearContext);
  if (!ctx) throw new Error('useAuditYear must be used inside <AuditYearProvider>');
  return ctx;
}
